import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { CustomerAuthService } from 'app/commons/services/customer-auth/customer-auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { environment } from 'environments/environment';
import { catchError, switchMap, throwError } from 'rxjs';

/**
 * Attaches the customer Bearer token ONLY to the Default Profile API calls
 * (and the customer endpoints that require it). All other requests keep using
 * the static x-api-key only. On 401 it tries a single silent refresh.
 */
const BEARER_PATH_PREFIXES = [
    `${environment.baseApi}/v1/open-api/default-attendee`,
    `${environment.baseApi}/v1/open-api/default-attendee-answer`,
    `${environment.baseApi}/v1/open-api/default-profile-on-factory`,
    `${environment.baseApi}/v1/open-api/default-profile-on-participant-representative`,
    `${environment.baseApi}/v1/customer-auth/me`,
    `${environment.baseApi}/v1/customer-auth/my-attendees`,
];

function needsBearer(url: string): boolean {
    return BEARER_PATH_PREFIXES.some((prefix) => url.startsWith(prefix));
}

export const customerAuthInterceptor: HttpInterceptorFn = (req, next) => {
    const auth = inject(CustomerAuthService);
    const router = inject(Router);

    let request = req;
    const token = auth.accessToken;
    if (needsBearer(req.url) && token && !AuthUtils.isTokenExpired(token)) {
        request = req.clone({
            headers: req.headers.set('Authorization', 'Bearer ' + token),
        });
    }

    return next(request).pipe(
        catchError((error: HttpErrorResponse) => {
            if (!needsBearer(req.url)) {
                return throwError(() => error);
            }

            // 401 → try one silent refresh, then retry the original request.
            if (error.status === 401 && auth.refreshToken) {
                return auth.refresh().pipe(
                    switchMap((pair) => {
                        const retried = req.clone({
                            headers: req.headers.set(
                                'Authorization',
                                'Bearer ' + pair.accessToken
                            ),
                        });
                        return next(retried);
                    }),
                    catchError((refreshError) => {
                        auth.signOut();
                        router.navigateByUrl('/profile/login');
                        return throwError(() => refreshError);
                    })
                );
            }

            if (error.status === 401) {
                auth.signOut();
                router.navigateByUrl('/profile/login');
            } else if (error.status === 403) {
                router.navigateByUrl('/profile/landing');
            }

            return throwError(() => error);
        })
    );
};
